import {
  getMislabelsThresholdRange,
  getSelectedQueryModal,
} from "redux/SingleDataset/selectors";
import {
  setSelectedQueryModal,
  clearFilters,
} from "redux/SingleDataset/actions";
import {
  createAndUpdateIssueTypesFilter,
  getExistingQueryByFilterType,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { ENTITY_TYPE_PARAMETER } from "helpers/constants/miscellaneous";
import { useState, RefObject, useEffect, useRef } from "react";
import Button from "views/components/QueryPanel/Button";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import FilterMenu from "views/components/FilterMenu";
import ErrorModal from "views/modals/ErrorModal";
import useQueryParam from "hooks/useQueryParam";
import useVqlParams from "hooks/useVqlParams";
import AddIcon from "assets/icons/AddIcon";

import {
  Filters,
  Position,
  QueryOperator,
  isValidEntityType,
  FilterMenuItemType
} from "types";
import { isCustomMetadata } from "helpers/utility/customMetadataHelpers";

import styles from "./style.module.scss";

interface AddFilterProps {
  rowRef: RefObject<HTMLDivElement>;
}

export default function AddFilter({ rowRef }: AddFilterProps) {
  const [btnPosition, setBtnPosition] = useState<Position | null>(null);
  const [showCaptionWarning, setShowCaptionWarning] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const { vqlQueries, addVqlQuery } = useVqlParams();
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeFromQueryParam = isValidEntityType(entityParam)
    ? entityParam
    : null;

  const selectedModal = useSelector(getSelectedQueryModal);
  const mislabelsRange = useSelector((state) =>
    getMislabelsThresholdRange(state, entityTypeFromQueryParam)
  );

  const btnRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { datasetId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const toggle = () => setShowMenu(!showMenu);

  
  

  const handleFilterSelection = (
    type: FilterMenuItemType,
    disabled: boolean
  ) => {
    if (disabled && type === Filters.CAPTION) {
      setShowCaptionWarning(true);
      return;
    }
    
    // Handle custom metadata filters
    if (isCustomMetadata(type)) {
      if (btnPosition) {
        dispatch(setSelectedQueryModal({ type, position: btnPosition }));
      }
      return;
    }

    //if thresholds donot exist for mislabels, do not open modal
    if (type === Filters.MISLABELS && !mislabelsRange) {
      const isApplied = vqlQueries.some(
        (q) =>
          isVqlQuery(q, "issues") &&
          q.issues.value === "mislabels" &&
          convertAPIOperator(q.issues?.mode) === QueryOperator.IS
      );

      if (isApplied) return;

      const vqlQuery = createAndUpdateIssueTypesFilter(
        "mislabels",
        QueryOperator.IS,
        [0, 1]
      );
      addVqlQuery(vqlQuery);
      return;
    }

  const existingFilter = getExistingQueryByFilterType(type, vqlQueries)
    let position = btnPosition;
    if (existingFilter) {
      const selector = `[data-query-id="${existingFilter.id}"]`;
      const el = document.querySelector(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        position = {
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        };
      }
    }
    dispatch(
      setSelectedQueryModal({
        type,
        position: position || undefined,
        id: existingFilter?.id,
      })
    );
  };

  const handleGotoEnrichment = () => {
    dispatch(clearFilters() as any);
    navigate(`/dataset/${datasetId!}/enrich`);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !menuRef.current?.contains(event.target as Node) &&
        !btnRef.current?.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const row = rowRef.current;
      const isRightAligned = row
        ? rect.left - row.getBoundingClientRect().left >
          row.getBoundingClientRect().width * 0.6
        : false;

      const top = rect.bottom + window.scrollY + 4;
      const left =
        rect.left + (isRightAligned ? rect.width : 0) + window.scrollX;

      if (menuRef.current) {
        menuRef.current.style.setProperty(
          "--menu-transform",
          isRightAligned ? "translate(-100%)" : "none"
        );
        menuRef.current.style.setProperty("--menu-top", `${top}px`);
        menuRef.current.style.setProperty("--menu-left", `${left}px`);

        setBtnPosition({ top, left });
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu, selectedModal, rowRef]);

  return (
    <div
      className={styles.addFilterBtn}
      data-testid="add-filter-btn"
      ref={btnRef}
      onClick={toggle}
    >
      <Button icon={<AddIcon />} title="Add Filter" onClick={toggle} />
      {showMenu && (
        <div
          className={styles.menuPositioner}
          data-testid="menu-positioner"
          ref={menuRef}
        >
          <FilterMenu onSelect={handleFilterSelection} />
        </div>
      )}

      <ErrorModal
        showModal={showCaptionWarning}
        title="No captions available for this dataset"
        message="To use search by caption, enrich your dataset using one of our Image-to-Text models. This will generate captions and unlock the caption filter."
        onClose={() => setShowCaptionWarning(false)}
        actionButton1={
          <div className={styles.goToEnrichment} onClick={handleGotoEnrichment}>
            Go to Enrichment
          </div>
        }
      />
    </div>
  );
}
